import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Send } from 'lucide-react';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import PollCard from '@/components/PollCard';

const PollDetailPage = () => {
  const { pollId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/polls/${pollId}`)
      .then((r) => {
        if (!cancelled) setPoll(r.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || 'Poll not found');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pollId, user]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50/40 to-white">
      <Navbar />
      <main className="container mx-auto max-w-2xl px-4 pt-24 pb-24" data-testid="poll-detail-page">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/polls')}
          className="mb-4 -ml-2"
          data-testid="poll-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Polls
        </Button>

        {loading && (
          <div className="text-center py-12 text-muted-foreground">Loading…</div>
        )}

        {!loading && error && (
          <div className="rounded-2xl bg-white border-2 border-red-100 p-8 text-center">
            <p className="font-bold mb-1">Couldn't load this poll</p>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        )}

        {!loading && poll && (
          <>
            <PollCard
              poll={poll}
              onUpdated={setPoll}
              onRemoved={() => navigate('/polls')}
            />

            {/* Quick-share deep links to WhatsApp / Telegram */}
            {(() => {
              const shareUrl = `${window.location.origin}/polls/${poll.poll_id}`;
              const text = `Vote on this poll: ${poll.question} — ${shareUrl}`;
              const waHref = `https://wa.me/?text=${encodeURIComponent(text)}`;
              const tgHref = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent('Vote on this poll: ' + poll.question)}`;
              return (
                <div className="mt-4 flex flex-wrap gap-2 justify-center" data-testid="poll-share-row">
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center h-11 px-5 rounded-full border-2 border-green-500 text-green-600 font-semibold hover:bg-green-50 transition-colors"
                    aria-label="Share on WhatsApp"
                    data-testid="poll-share-whatsapp-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </a>
                  <a
                    href={tgHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center h-11 px-5 rounded-full border-2 border-sky-500 text-sky-600 font-semibold hover:bg-sky-50 transition-colors"
                    aria-label="Share on Telegram"
                    data-testid="poll-share-telegram-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Telegram
                  </a>
                </div>
              );
            })()}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PollDetailPage;
